// LineagePage: surface FD-DAG discovery results.
//
// What this page actually does (despite the historical name):
// **exploratory FD-DAG discovery**. The AC author picks a table from
// their AC Columns pool, hits the button, and the backend empirically
// tests every column pair in that table and returns the FD edges that
// hold in the data — i.e., which columns determine which.
//
// Why an AC author wants this: BEFORE picking anchor A on the Declare
// page, they need to know which other columns functionally determine
// each metric/dim. Lineage surfaces those candidate anchors. Once
// anchors are *declared*, the Attest page verifies them with the same
// underlying FD-edge attestation, scoped to one edge at a time.
//
// Scope: limited to the tables in the AC Columns pool. Running
// FD-DAG on tables the user didn't pick is wasted work; the discovery
// op tests O(n²) column pairs.

import { useEffect, useMemo, useState } from "react";
import { ApiError } from "../api/client";
import type { DraftResponse } from "../api/client";
import type {
  ColumnRef,
  DiscoverFDDAGResult,
} from "../api/types";
import { useSession } from "./SessionContext";

export function LineagePage() {
  const { client, session, refreshSession } = useSession();
  const [selected, setSelected] = useState<string | null>(null);
  const [columns, setColumns] = useState<string>("");   // comma-separated
  const [result, setResult] = useState<DiscoverFDDAGResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Draft AC — used to surface AC-declared mapping_derived edges
  // (Shape A derived dims), which aren't data-discoverable since
  // they're computed at query time, not observed in any backend
  // table.
  const [draft, setDraft] = useState<DraftResponse | null>(null);

  // Derive the in-scope tables + per-table column list from the
  // session's column_pool. Recomputes whenever the pool changes.
  const { poolTables, poolColumnsByTable } = useMemo(() => {
    const pool: ColumnRef[] = session?.column_pool ?? [];
    const byTable = new Map<string, string[]>();
    for (const ref of pool) {
      const cols = byTable.get(ref.table) ?? [];
      cols.push(ref.column);
      byTable.set(ref.table, cols);
    }
    return {
      poolTables: Array.from(byTable.keys()).sort(),
      poolColumnsByTable: byTable,
    };
  }, [session?.column_pool]);

  // Load draft AC once a session is active. Note: we no longer call
  // ListTables — the table dropdown is sourced from the column_pool,
  // not from the full backend. Scope-by-AC-pool is the whole point of
  // the workbench redesign §4.
  useEffect(() => {
    if (session === null) {
      setDraft(null);
      return;
    }
    client
      .getDraft(session.session_id)
      .then((d) => setDraft(d))
      .catch(() => setDraft(null));  // non-fatal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, session?.session_id]);

  // When the user picks a (different) table, pre-fill the columns
  // input with the pool's columns for that table. Saves the author
  // from typing them out; running FD-DAG on columns they didn't pick
  // would be wasted O(n²) work.
  useEffect(() => {
    if (selected === null) {
      setColumns("");
      return;
    }
    const cols = poolColumnsByTable.get(selected) ?? [];
    setColumns(cols.join(", "));
  }, [selected, poolColumnsByTable]);

  async function handleDiscover() {
    if (session === null || selected === null) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const cols = columns
        .split(",")
        .map((c) => c.trim())
        .filter((c) => c.length > 0);
      const inputs: Record<string, unknown> = { table: selected };
      if (cols.length > 0) {
        inputs.columns = cols;
      }
      const r = await client.runOp(session.session_id, "DiscoverFDDAG", { inputs });
      if (!r.success || !r.result) {
        throw new Error(r.error ?? "DiscoverFDDAG failed");
      }
      setResult(r.result as unknown as DiscoverFDDAGResult);
      refreshSession();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  }

  if (session === null) {
    return (
      <section>
        <h1>Lineage</h1>
        <p style={{ color: "#888" }}>
          Create a session on the <em>AC Session</em> tab to discover FD-DAGs.
        </p>
      </section>
    );
  }

  if (poolTables.length === 0) {
    return (
      <section>
        <h1>Lineage</h1>
        <p style={subtitleStyle}>
          FD-DAG discovery — find the functional dependencies among a
          table's columns via data attestation.
        </p>
        <div style={emptyStateStyle}>
          <p style={{ margin: 0 }}>
            No tables in scope. Pick the columns this AC should cover on
            the <strong>AC Columns</strong> tab first; this page then
            shows the tables those columns live in, so you can discover
            the FD-DAG structure that informs your anchor choices.
          </p>
        </div>
      </section>
    );
  }

  const tablePoolCount = poolColumnsByTable.get(selected ?? "")?.length ?? 0;

  return (
    <section>
      <h1>Lineage</h1>
      <p style={subtitleStyle}>
        FD-DAG discovery — find the functional dependencies among the
        in-scope columns of a table via data attestation. Use this
        <em> before</em> declaring anchors; the FD edges discovered
        here are your candidate anchors on the <em>Declare</em> page.
      </p>

      <div style={formRowStyle}>
        <label style={labelStyle}>
          table (in AC Columns scope)
          <select
            value={selected ?? ""}
            onChange={(e) => setSelected(e.target.value || null)}
            style={selectStyle}
          >
            <option value="">— pick a table —</option>
            {poolTables.map((t) => {
              const n = poolColumnsByTable.get(t)?.length ?? 0;
              return (
                <option key={t} value={t}>
                  {t} ({n} col{n === 1 ? "" : "s"})
                </option>
              );
            })}
          </select>
        </label>
        <label style={labelStyle}>
          columns (pre-filled from your pool — edit to restrict further)
          <input
            value={columns}
            onChange={(e) => setColumns(e.target.value)}
            placeholder="store_id, city, region"
            style={inputStyle}
          />
        </label>
        <button
          onClick={handleDiscover}
          disabled={busy || selected === null || tablePoolCount === 0}
          style={buttonStyle}
        >
          {busy ? "Discovering…" : "Discover FD-DAG"}
        </button>
      </div>

      {error && (
        <div style={errorBlockStyle}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {result && <FDReportView result={result} />}

      {/* AC-declared mapping-derived edges — these don't appear in
          data-discovered FD-DAGs because they're not in any backend
          table; they exist in the AC and execute via the engine's
          Branch 2b. See drafts/coframe_derived_dimensions_design_v0_1.md. */}
      {draft !== null && draft.draft_ac !== null && (
        <MappingDerivedEdgesSection draft={draft.draft_ac} />
      )}
    </section>
  );
}

/** Render mapping_derived edges declared on the AC's dimension
 *  families. Hidden when no derived dimensions are declared. */
function MappingDerivedEdgesSection({
  draft,
}: {
  draft: NonNullable<DraftResponse["draft_ac"]>;
}) {
  type Edge = {
    family: string;
    head: string;
    tail: string;
    mapping_size: number;
    default: string | null | undefined;
  };
  const edges: Edge[] = [];
  for (const df of draft.dimension_families) {
    if (!df.derived_dimensions) continue;
    for (const dd of df.derived_dimensions) {
      edges.push({
        family: df.name,
        head: dd.derived_from,
        tail: dd.name,
        mapping_size: Object.keys(dd.mapping).length,
        default: dd.default,
      });
    }
  }
  if (edges.length === 0) return null;

  return (
    <div style={{ marginTop: "2rem" }}>
      <h2 style={{ borderTop: "1px solid #ddd", paddingTop: "1rem" }}>
        AC-Declared Mapping-Derived Edges ({edges.length})
      </h2>
      <p style={{ color: "#666", fontSize: "0.9rem", marginTop: 0 }}>
        FD-edges added by the AC's derived dimensions — not
        data-discoverable (no backend column to inspect). The mapping
        is applied at query time by the engine's Branch 2b; the result
        is never materialised (see{" "}
        <code>drafts/coframe_derived_dimensions_design_v0_1.md</code>).
      </p>
      <table style={edgeTableStyle}>
        <thead>
          <tr>
            <th style={edgeThStyle}>edge</th>
            <th style={edgeThStyle}>family</th>
            <th style={edgeThStyle}>mapping size</th>
            <th style={edgeThStyle}>unmapped behavior</th>
          </tr>
        </thead>
        <tbody>
          {edges.map((e) => (
            <tr key={`${e.family}/${e.head}->${e.tail}`}>
              <td style={edgeTdStyle}>
                <code>{e.head}</code> → <code>{e.tail}</code>
              </td>
              <td style={edgeTdStyle}>{e.family}</td>
              <td style={edgeTdStyle}>{e.mapping_size} entries</td>
              <td style={edgeTdStyle}>
                {e.default !== undefined && e.default !== null ? (
                  <>
                    default → <code>{e.default}</code>
                  </>
                ) : (
                  <span style={{ color: "#888" }}>strict (raise)</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const edgeTableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.9rem",
};
const edgeThStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "0.4rem 0.6rem",
  borderBottom: "1px solid #ddd",
  fontWeight: 600,
  color: "#444",
};
const edgeTdStyle: React.CSSProperties = {
  padding: "0.4rem 0.6rem",
  borderBottom: "1px solid #eee",
};

// ── Result view ──────────────────────────────────────────────────────

function FDReportView({ result }: { result: DiscoverFDDAGResult }) {
  const r = result.report;
  return (
    <div style={resultBlockStyle}>
      <div style={resultHeaderStyle}>
        <h2 style={{ margin: 0 }}>
          <code>{r.table}</code>
        </h2>
        <div style={metricsRowStyle}>
          <Metric label="rows" value={r.row_count.toLocaleString()} />
          <Metric label="columns" value={String(r.columns_analyzed.length)} />
          <Metric
            label="pairs tested / pruned"
            value={`${r.pairs_tested} / ${r.pairs_skipped}`}
          />
          {r.priors_received > 0 && (
            <Metric
              label="priors confirmed"
              value={`${r.priors_confirmed} / ${r.priors_received}`}
            />
          )}
          {result.cached && (
            <span style={cachedBadgeStyle} title="From session cache">
              cached
            </span>
          )}
        </div>
      </div>

      {r.constants.length > 0 && (
        <Block title={`Constants (${r.constants.length})`} hint="Single-value columns; excluded from FD analysis.">
          <CodeList items={r.constants} />
        </Block>
      )}

      {r.keys.length > 0 && (
        <Block title={`Keys (${r.keys.length})`} hint="Columns whose nunique == row_count.">
          <CodeList items={r.keys} />
        </Block>
      )}

      {r.equivalence_classes.length > 0 && (
        <Block
          title={`Equivalence classes (${r.equivalence_classes.length})`}
          hint="Mutually-determining columns (1-to-1); merged into one node in the reduced DAG."
        >
          <ul style={listStyle}>
            {r.equivalence_classes.map((eq, i) => (
              <li key={i}>
                <code>{eq.members.join(" ≡ ")}</code>
                <span style={{ color: "#666" }}>
                  {" — representative: "}
                  <code>{eq.representative}</code>
                </span>
              </li>
            ))}
          </ul>
        </Block>
      )}

      <Block
        title={`Reduced DAG (${r.reduced_edges.length} edges)`}
        hint="Minimum transitive cover. Edges implied by other paths are dropped."
      >
        {r.reduced_edges.length === 0 ? (
          <p style={{ color: "#888" }}>
            No FD edges discovered (within the chosen column set).
          </p>
        ) : (
          <ul style={listStyle}>
            {r.reduced_edges.map(([a, c], i) => (
              <li key={i}>
                <code>{a}</code> <span style={{ color: "#888" }}>→</span>{" "}
                <code>{c}</code>
              </li>
            ))}
          </ul>
        )}
      </Block>

      <details style={detailsStyle}>
        <summary style={summaryStyle}>
          All discovered FDs (pre-reduction, {r.discovered_fds.length})
        </summary>
        <table style={fdTableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>X</th>
              <th style={thStyle}>→</th>
              <th style={thStyle}>Y</th>
              <th style={thStyle}>warrant</th>
              <th style={thStyleNumeric}>|X|</th>
              <th style={thStyleNumeric}>violations</th>
            </tr>
          </thead>
          <tbody>
            {r.discovered_fds.map((d, i) => (
              <tr key={i}>
                <td style={tdStyle}><code>{d.antecedent}</code></td>
                <td style={tdStyle}>{d.holds ? "→" : "↛"}</td>
                <td style={tdStyle}><code>{d.consequent}</code></td>
                <td style={tdStyle}>
                  <span style={warrantBadgeStyle(d.warrant_source)}>
                    {d.warrant_source === "code-affirmed-prior"
                      ? "code-affirmed"
                      : "data-discovery"}
                  </span>
                </td>
                <td style={tdStyleNumeric}>{d.distinct_antecedent_count.toLocaleString()}</td>
                <td style={tdStyleNumeric}>{d.violation_count.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

function Block({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={blockStyle}>
      <h3 style={blockTitleStyle}>{title}</h3>
      {hint && <p style={hintStyle}>{hint}</p>}
      {children}
    </div>
  );
}

function CodeList({ items }: { items: string[] }) {
  return (
    <ul style={listStyle}>
      {items.map((it) => (
        <li key={it}>
          <code>{it}</code>
        </li>
      ))}
    </ul>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div style={metricStyle}>
      <div style={metricLabelStyle}>{label}</div>
      <div style={metricValueStyle}>{value}</div>
    </div>
  );
}

function formatError(e: unknown): string {
  if (e instanceof ApiError) return `${e.status} ${e.message}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

// ── Styles ────────────────────────────────────────────────────────────

const subtitleStyle: React.CSSProperties = { color: "#888", margin: "0 0 1rem 0" };
const emptyStateStyle: React.CSSProperties = {
  padding: "1.25rem 1.5rem",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 6,
  color: "#444",
  fontSize: "0.9rem",
};
const formRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "1rem",
  alignItems: "flex-end",
  marginBottom: "1rem",
};
const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  fontSize: "0.85rem",
  color: "#444",
};
const inputStyle: React.CSSProperties = {
  padding: "0.4rem 0.6rem",
  fontSize: "0.95rem",
  border: "1px solid #ccc",
  borderRadius: 4,
  minWidth: "20rem",
};
const selectStyle: React.CSSProperties = {
  ...inputStyle,
  minWidth: "14rem",
};
const buttonStyle: React.CSSProperties = {
  padding: "0.5rem 1rem",
  background: "#0366d6",
  color: "white",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: "0.95rem",
};
const errorBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  background: "#ffeef0",
  border: "1px solid #f3c0c0",
  borderRadius: 4,
  marginTop: "1rem",
  fontSize: "0.9rem",
};
const resultBlockStyle: React.CSSProperties = { marginTop: "1rem" };
const resultHeaderStyle: React.CSSProperties = { marginBottom: "1rem" };
const metricsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "1.5rem",
  marginTop: "0.5rem",
  alignItems: "center",
};
const metricStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
};
const metricLabelStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
};
const metricValueStyle: React.CSSProperties = {
  fontSize: "0.95rem",
  fontWeight: 500,
};
const cachedBadgeStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  background: "#fff5d6",
  color: "#8b6914",
  padding: "0.1rem 0.4rem",
  borderRadius: 3,
  border: "1px solid #e6d8a8",
  marginLeft: "auto",
};
const blockStyle: React.CSSProperties = {
  marginBottom: "1.25rem",
  padding: "0.8rem 1rem",
  background: "#fafafa",
  border: "1px solid #ddd",
  borderRadius: 4,
};
const blockTitleStyle: React.CSSProperties = { margin: "0 0 0.25rem 0", fontSize: "1rem" };
const hintStyle: React.CSSProperties = {
  margin: "0 0 0.5rem 0",
  color: "#888",
  fontSize: "0.85rem",
};
const listStyle: React.CSSProperties = { margin: 0, paddingLeft: "1.2rem" };
const detailsStyle: React.CSSProperties = { marginTop: "1rem" };
const summaryStyle: React.CSSProperties = { cursor: "pointer", color: "#0366d6" };
const fdTableStyle: React.CSSProperties = {
  width: "100%",
  marginTop: "0.5rem",
  borderCollapse: "collapse",
  fontSize: "0.85rem",
};
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "0.3rem 0.5rem",
  background: "#f0f0f0",
  borderBottom: "1px solid #ddd",
  fontSize: "0.75rem",
  color: "#666",
};
const thStyleNumeric: React.CSSProperties = { ...thStyle, textAlign: "right" };
const tdStyle: React.CSSProperties = {
  padding: "0.25rem 0.5rem",
  borderBottom: "1px solid #eee",
};
const tdStyleNumeric: React.CSSProperties = { ...tdStyle, textAlign: "right" };

function warrantBadgeStyle(warrant: string): React.CSSProperties {
  const palette: Record<string, [string, string]> = {
    "code-affirmed-prior": ["#e9f5e9", "#1a6b3a"],
    "data-discovery": ["#e3edf9", "#1c4e80"],
  };
  const [bg, fg] = palette[warrant] ?? ["#f0f0f0", "#666"];
  return {
    background: bg,
    color: fg,
    padding: "0.1rem 0.4rem",
    borderRadius: 3,
    fontSize: "0.75rem",
  };
}
